/**
 * The theme's single review-carousel behaviour.
 *
 * Resolves D-001: `sections/ratings.liquid` and `snippets/pdp-testimonials.liquid`
 * are the same component — a horizontal scroll track of `.rev-card` with
 * prev/next arrows — built twice.
 *
 * D-001's table needs one correction: it recorded ratings.liquid as having no
 * drag-to-scroll. It does. Both have identical drag handlers. The only real
 * behavioural difference is autoplay, which ratings has (4s, pause on
 * hover/touch) and the PDP does not.
 *
 * TWO BUGS SHARED BY BOTH, fixed here
 * -----------------------------------
 * 1. `parseInt(getComputedStyle(track).gap || 18)`
 *
 *    The `||` guards the STRING, not parseInt's result. `gap` computes to
 *    "18px" when a gap is set and "normal" when it is not — and "normal" is
 *    truthy, so the fallback never applies. `parseInt("normal")` is NaN, so
 *    CARD_W() returns NaN, and `scrollBy({ left: NaN })` silently does nothing.
 *    The fallback was unreachable in exactly the case it existed for: the
 *    arrows would have been dead, with no error in the console.
 *
 * 2. Autoplay paused on hover and NEVER RESUMED.
 *
 *    `clearInterval` on mouseenter with no matching mouseleave, so one stray
 *    mouse movement across the track killed autoplay for the rest of the page's
 *    life. The comment says "Pause", which is not what code that only stops
 *    does.
 *
 * Neither is visible to the parity harness: it stubs `setInterval` before page
 * scripts run (autoplay would make every capture a different page) and it never
 * clicks anything.
 *
 * The per-instance gap and card-width fallbacks stay per-instance — 18/338 for
 * ratings, 16/316 for the PDP — because they describe two differently sized
 * cards, not a disagreement to resolve.
 */
(function () {
  /**
   * @param {object} cfg
   * @param {string} cfg.track            element id of the scroll track
   * @param {string} cfg.prev             element id of the previous arrow
   * @param {string} cfg.next             element id of the next arrow
   * @param {number} cfg.gapFallback      px gap when the computed value is unusable
   * @param {number} cfg.cardFallback     px card width when there is no card to measure
   * @param {boolean} [cfg.autoplay]
   * @param {number} [cfg.interval]       ms between advances, default 4000
   */
  function bind(cfg) {
    const track = document.getElementById(cfg.track);
    const prev = document.getElementById(cfg.prev);
    const next = document.getElementById(cfg.next);
    if (!track || !prev || !next) return;

    /** Width of one card plus the gap after it. */
    const cardWidth = () => {
      const first = track.querySelector('.rev-card');
      if (!first) return cfg.cardFallback;
      // Parse FIRST, then decide whether the result is usable — the inverse of
      // the original, and the whole of bug 1.
      const gap = parseInt(getComputedStyle(track).gap, 10);
      return first.offsetWidth + (Number.isNaN(gap) ? cfg.gapFallback : gap);
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -cardWidth(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: cardWidth(), behavior: 'smooth' }));

    if (cfg.autoplay) {
      let timer = null;
      const advance = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= maxScroll - 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
        }
      };
      const start = () => { stop(); timer = setInterval(advance, cfg.interval ?? 4000); };
      const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

      start();
      track.addEventListener('mouseenter', stop);
      track.addEventListener('mouseleave', start);   // the missing half
      track.addEventListener('touchstart', stop, { passive: true });
      // A tab in the background should not keep scrolling; the hero carousel
      // already does this and the review one did not.
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    }

    // Drag to scroll. `preventDefault` on mousedown stops the browser starting
    // a text selection, which the original allowed — dragging the track
    // highlighted the review text as it moved.
    let dragging = false;
    let startX = 0;
    let scrollStart = 0;

    track.addEventListener('mousedown', (e) => {
      dragging = true;
      startX = e.pageX;
      scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
      e.preventDefault();
    });
    document.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      track.scrollLeft = scrollStart - (e.pageX - startX);
    });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
    };
    document.addEventListener('mouseup', endDrag);
    // Releasing outside the window left `is-dragging` stuck on, so the track
    // kept following the cursor after the button was up.
    window.addEventListener('blur', endDrag);
  }

  window.GlowmingCarousel = { bind };
})();
